/* eslint-disable jsx-a11y/anchor-is-valid */
import { useEffect, useState } from 'react';
import { DataGridPro, GridActionsCellItem } from '@mui/x-data-grid-pro';
import apiAdmin from '../../services/api-admin';
import { MdEdit, MdDelete, MdAdd, MdContentCopy } from 'react-icons/md';
import { useForm, Controller} from "react-hook-form";
import { Dropzone } from "dropzone";
import "dropzone/dist/dropzone.css";
import { Editor } from 'react-draft-wysiwyg';
import './../styles/react-draft-wysiwyg.css';
import draftToHtml from 'draftjs-to-html';
import { convertToRaw, EditorState} from 'draft-js';
import './community-events.scss';
import { FilePath, FormatHTMLForWYSIWYGEditor, } from '../../services/utilities';
import { TextField } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {DateTime} from 'luxon';
import OutlinedDiv from '../layout/OutlinedDiv';
import { useOktaAuth } from '@okta/okta-react';
import CommunityEventDetails from './community-event-details';
import CustomDataGridToolbar from '../layout/custom-toolbar';

const CommunityEvents = (props) => {
    const { register, handleSubmit, setValue, getValues, reset, formState: { errors }, control  } = useForm();
    const { authState } = useOktaAuth();
    const [communityEvents, setCommunityEvents] = useState([]);
    const [communityEventIdToEdit, setCommunityEventIdToEdit] = useState(null);
    const [communityEventToEdit, setCommunityEventToEdit] = useState(null);
    const [communityEventIdToDelete, setCommunityEventIdToDelete] = useState(null);
    const [communityEventDetails, setCommunityEventDetails] = useState([]);
    const [imageDropzone, setImageDropzone] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [deleted, setDeleted] = useState(false);
    const [error, setError] = useState(null);
   
    const [contactEditorState, setContactEditorState] = useState(
        () => EditorState.createEmpty(),
    )
    const [descriptionEditorState, setDescriptionEditorState] = useState(
        () => EditorState.createEmpty(),
    )
    const [sections, setSections] = useState([]);

    const handleSectionsChange = (newSections) => {
        setSections(newSections);
    };

    const columns = [
        { field: 'title', headerName: 'Title', flex: 750},
        { field: 'activeDate', headerName: 'Date', flex: 150, renderCell: function (t) { return t.value.toLocaleDateString("en-US")} },
        { field: 'active', headerName: 'Active', flex: 200 },
        {
            field: 'actions',
            type: 'actions',
            headerName: 'Actions',
            flex: 100,
            cellClassName: 'actions',
            getActions: ({ id }) => {

                return [
                    <GridActionsCellItem
                        icon={<MdEdit />}
                        label="Edit"
                        onClick={() => { setCommunityEventIdToEdit(id)}}
                        color="inherit"
                    />,
                    <GridActionsCellItem
                        icon={<MdDelete />}
                        label="Delete"
                        onClick={() => { setCommunityEventIdToDelete(id); setDeleted(false)  }}
                        color="inherit"
                        data-bs-toggle="modal" 
                        data-bs-target="#deleteModal" role="button" 
                    />,
                    <GridActionsCellItem
                        icon={<MdContentCopy title="Copy" />}
                        label="Copy"
                        onClick={() => { copyEvent(id) }}
                        color="inherit"
                    />,    
                ];
            }
        }
    ];

    const copyEvent = id => {
        setSubmitted(false);
        const getEvent = async () => {
            const response = await apiAdmin.get(`/community-event?communityEventId=${id}`);
            const copiedEvent = { ...response.data };
            copiedEvent.eventId = -1;
            copiedEvent.title = copiedEvent.title + ' (COPY)';

            if (copiedEvent.sectionDetails) {
                copiedEvent.sectionDetails = copiedEvent.sectionDetails.map(detail => ({
                    ...detail,
                    eventDetailId: -1,
                    eventId: -1
                }));
            }

            const saveResponse = await apiAdmin.post(`/save-community-event`, copiedEvent);
            if (saveResponse.data) {
                setSubmitted(true);
                reset();
            } else {
                setError("There was an error copying the event. Please try again.");
            }
        };
        getEvent();
    };

    useEffect(() => {
        const getCommunityEvents = async () => {
            apiAdmin.get(`/community-events`)
                .then((response) => {
                    setCommunityEvents(response.data.map((a) => ({ id: a.eventId, title: a.title, active: (a.active ? "Yes" : "No"), activeDate: new Date(a.activeDate) })));
                });
        }
        getCommunityEvents();

    }, [submitted, deleted]);

    useEffect(() => {
        if (!communityEventIdToEdit || communityEventIdToEdit === -1) return;
        const getCommunityEvent = async () => {
            apiAdmin.get(`/community-event?communityEventId=${communityEventIdToEdit}`)
                .then((response) => {
                    setImageDropzone(null);
                    setCommunityEventToEdit(response.data);
                    setValue('title', response.data.title);
                    setValue('activeDate', DateTime.fromISO(response.data.activeDate));
                    setValue('dateDetail', response.data.dateDetail);
                    setValue('location', response.data.location);
                    setValue('beneficiary', response.data.beneficiary);
                    setValue('contact', response.data.contact);
                    setValue('description', response.data.description);
                    setValue('registrationLink', response.data.registrationLink);
                    setValue('pagePath', response.data.pagePath);
                    setValue('active', response.data.active);

                    if (response.data.sectionDetails) {
                        const initialSections = response.data.sectionDetails.map(detail => {
                            return {
                                detailId: detail.eventDetailId,
                                title: detail.eventDetailTitle,
                                eventDetail: detail.eventDetail 
                            };
                        });
                        setCommunityEventDetails(initialSections);
                    }
                });
        }
        getCommunityEvent();
    }, [communityEventIdToEdit]);

    useEffect(() => {
        if (communityEventToEdit || communityEventIdToEdit === -1) {
            setSubmitted(false);
            if (imageDropzone) imageDropzone.disable();
            setImageDropzone(new Dropzone("div#image", { url: process.env.REACT_APP_API_ADMIN_BASE_URL + "/community-event/image", acceptedFiles: '.webp, .jpg', createImageThumbnails: true, maxFilesize: 15, maxFiles: 1, headers: { 'Ocp-Apim-Subscription-Key': process.env.REACT_APP_API_ADMIN_SUBSCRIPTION_KEY, 'Authorization': `Bearer ${authState?.accessToken?.accessToken}` } }));
            if (communityEventToEdit) {
                setContactEditorState(FormatHTMLForWYSIWYGEditor(communityEventToEdit.contact));
                setDescriptionEditorState(FormatHTMLForWYSIWYGEditor(communityEventToEdit.description));
            } else {
                setContactEditorState(EditorState.createEmpty());
                setDescriptionEditorState(EditorState.createEmpty());
            }
        }
    }, [communityEventToEdit, communityEventIdToEdit]);

    function EditToolbar(props) {
        return (
            <CustomDataGridToolbar className="d-flex justify-content-between">
                <span className="btn-link btn" onClick={() => {setCommunityEventIdToEdit(-1)} }>
                    <MdAdd className="mb-1" /> Add Community Event
                </span>
                <span>
                    Please note: One entry is allowed per event.
                </span>
            </CustomDataGridToolbar>
        );
    }
    const onSubmit = data => {
        const sectionsData = sections.map(section => ({
            eventDetailId: section.detailId,
            eventDetailTitle: section.title,
            eventDetail: draftToHtml(convertToRaw(section.editorState.getCurrentContent()))
        }));

        const eventObject = {
            ...data,
            contact: draftToHtml(convertToRaw(contactEditorState.getCurrentContent())),
            description: draftToHtml(convertToRaw(descriptionEditorState.getCurrentContent())),
            imageName: imageDropzone.getAcceptedFiles().length > 0 ? imageDropzone.getAcceptedFiles()[0].name : '',
            eventId: communityEventIdToEdit,
            sectionDetails: sectionsData
        }

        const saveCommunityEvent = async () => {
            await apiAdmin.post(`/save-community-event`, eventObject)
                .then((response) => {
                    if (response.data) {
                        setCommunityEventIdToEdit(null);
                        setCommunityEventToEdit(null);
                        setCommunityEventDetails(null);
                        setSubmitted(true);
                        reset();
                    } else { 
                        setError("There was an error saving the event. Please try again.")
                    }
                });
        }
        saveCommunityEvent();
    }

    const deleteCommunityEvent = () => {
        const deleteEvent = async () => {
            await apiAdmin.post(`/delete-community-event?communityEventId=` + communityEventIdToDelete)
                .then((response) => {
                    if (response.statusText === "OK") {
                        setCommunityEventIdToDelete(null);
                        setDeleted(true);
                    } else { 
                        setError("There was an error deleting the event. Please try again.")
                    }
                });
        }
        deleteEvent();
    }
    
    return (
        <div>
            <title>Community Events</title>
            <h1>Community Events</h1>
            {communityEventToEdit || communityEventIdToEdit === -1 ? <>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="mb-3">
                        <div className="form-check form-switch">
                            <label className="form-check-label" htmlFor="active">Active</label>
                            <input className="form-check-input" type="checkbox" id="active" {...register("active")} />
                        </div>
                    </div>
                    <div className="mb-3">
                        <div className="row">
                            <div className="col-6">
                                <TextField
                                    label="Title *"
                                    {...register("title", {required: true})}
                                    className="w-100"
                                />
                                {errors?.title?.type === "required" && <small className="text-danger">This field is required</small>}
                             </div>
                            <div className="col-6">
                                <Controller
                                    name="activeDate"
                                    control={control}
                                    rules={ { validate: { dateValid: () =>  DateTime.fromISO(getValues("activeDate")).isValid}}}
                                    render={({ field: { onChange, value } }) => (
                                        <DatePicker value={value} onChange={onChange} className="w-100" label="Date *" />
                                    )}
                                />
                                {errors?.activeDate?.type === "dateValid" && <small className="text-danger">This field is required</small>}
                            </div>
                        </div>
                    </div>
                    <div className="mb-3">
                        <div className="row">
                            <div className="col-6">
                                <TextField
                                    label="Date/Time Details * (e.g. 8:00 AM check-in with a 10:00 AM start)"
                                    {...register("dateDetail", { required: true })}
                                    className="w-100"
                                />
                                {errors?.dateDetail?.type === "required" && <small className="text-danger">This field is required</small>}
                            </div>
                            <div className="col-6">
                                <TextField
                                    label="Location *"
                                    {...register("location", { required: true })}
                                    className="w-100"
                                />
                                {errors?.location?.type === "required" && <small className="text-danger">This field is required</small>}
                            </div>
                        </div>
                    </div>
                    <div className="mb-3">
                        <div className="row">
                            <div className="col-6">
                                <TextField
                                    label="Beneficiary *"
                                    {...register("beneficiary", { required: true })}
                                    className="w-100"
                                />
                                {errors?.beneficiary?.type === "required" && <small className="text-danger">This field is required</small>}
                            </div>
                            <div className="col-6">
                                <TextField
                                    label="Full Registration Link (e.g. 'https://titleonego.com/golf')"
                                    {...register("registrationLink")}
                                    className="w-100"
                                />
                            </div>
                        </div>
                    </div>
                    <div className="mb-3">
                        <TextField
                            label="Unique Page Path * (e.g. 'burley-cornhole')"
                            {...register("pagePath", { required: true })}
                            className="w-100"
                        />
                        {errors?.pagePath?.type === "required" && <small className="text-danger">This field is required</small>}
                    </div>
                    <div>
                        <div className="mb-3">
                            <OutlinedDiv label="TitleOne Contact Info">
                                <small> Please paste plain text only.</small>
                                <Editor
                                    editorState={contactEditorState}
                                    editorClassName="wysiwyg-contact"
                                    onEditorStateChange={(e) => setContactEditorState(e)}
                                />
                            </OutlinedDiv>
                        </div>
                        <div className="mb-3">
                            <OutlinedDiv label="Description">
                                <small> Please paste plain text only.</small>
                                <Editor
                                    editorState={descriptionEditorState}
                                    editorClassName="wysiwyg"
                                    onEditorStateChange={(e) => setDescriptionEditorState(e)}
                                    />
                            </OutlinedDiv>
                        </div>
                        <div className="mb-3">
                            <OutlinedDiv label="Image (.webp files only)">
                                <div id="image" className="w-100 dropzone dropzone-previews border-0">
                                    <div className="dz-message" data-dz-message><span>Image Upload</span></div>
                                </div>
                            </OutlinedDiv>
                        </div>
                        <div className="mb-3">
                            {communityEventToEdit && communityEventToEdit.imageName && <img src={FilePath('/communityeventimages/' + communityEventToEdit.imageName)} width="300" alt='Community Event' />}
                        </div>
                    </div>
                    <CommunityEventDetails
                        onSectionsChange={handleSectionsChange}
                        initialSections={communityEventDetails}
                    />
                    <div className="pt-2">
                        <button type="submit" className="btn btn-primary me-4">Submit</button>
                        <button type="button" className="btn btn-primary" onClick={() => { setCommunityEventToEdit(null); setCommunityEventIdToEdit(null); setCommunityEventDetails(null); setSubmitted(false); setError(null); reset(); } }>Cancel</button>
                    </div>
                </form>
            </>
            :
            <>
                {submitted &&  <div className="alert alert-success alert-dismissible" role="alert">Event saved successfully!<button type="button" className="btn-close" onClick={() => setSubmitted(false)} aria-label="Close"></button></div>}
                {deleted &&  <div className="alert alert-warning alert-dismissible" role="alert">Event deleted successfully!<button type="button" className="btn-close" onClick={() => setDeleted(false)}aria-label="Close"></button></div>}
                {error && <div className="alert alert-danger" role="alert">{error}</div>}
                <DataGridPro
                    rows={communityEvents}
                    columns={columns}
                    loading={communityEvents.length === 0}
                    slotProps={{
                        loadingOverlay: {
                            variant: 'skeleton',
                            noRowsVariant: 'skeleton',
                        },
                    }}
                    autoHeight
                    rowHeight={38}
                    disableRowSelectionOnClick
                    alignItems="center"
                    slots={{
                        toolbar: EditToolbar,
                    }}
                    initialState={{
                        pagination: {
                          paginationModel: { pageSize: 25, page: 0 },
                        },
                      }}
                    pagination
                    showToolbar />
            </>
            }
            <div className="modal shown" tabIndex="-1" id="deleteModal" >
                <div className="modal-dialog">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Delete Event?</h5>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                        </div>
                        <div className="modal-body">
                            <p>Are you sure you want to delete this event?</p>
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">No, Cancel</button>
                            <button type="button" className="btn btn-primary" data-bs-dismiss="modal" onClick={() => deleteCommunityEvent()}>Yes, Delete</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default CommunityEvents;
